import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { AlertTriangle, Clock, XCircle, Package, Trash2, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminScadenze = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllBatches();
  }, []);

  const fetchAllBatches = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/all-batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(response.data || []);
    } catch (error) {
      console.error("Errore:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBatch = async (productId, batchId) => {
    if (!window.confirm("Eliminare questo lotto?")) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/products/${productId}/batches/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lotto eliminato");
      fetchAllBatches();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const expired = batches.filter(b => b.is_expired);
  const expiringSoon = batches.filter(b => !b.is_expired && b.days_left <= 5);
  const expiringMid = batches.filter(b => !b.is_expired && b.days_left > 5 && b.days_left <= 12);
  const safe = batches.filter(b => !b.is_expired && b.days_left > 12);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  const getUrgency = (batch) => {
    if (batch.is_expired) return "expired";
    if (batch.days_left <= 5) return "critical";
    if (batch.days_left <= 12) return "warning";
    return "safe";
  };

  const BatchRow = ({ batch }) => {
    const urgency = getUrgency(batch);
    const bgColor = urgency === "expired" ? "bg-red-500/10 border-red-500/40" 
      : urgency === "critical" ? "bg-orange-500/10 border-orange-500/40" 
      : urgency === "warning" ? "bg-yellow-500/10 border-yellow-500/40"
      : "bg-zinc-800/50 border-zinc-700/40";
    
    const textColor = urgency === "expired" ? "text-red-400" 
      : urgency === "critical" ? "text-orange-400" 
      : urgency === "warning" ? "text-yellow-400"
      : "text-green-400";

    const scadFormatted = new Date(batch.scadenza).toLocaleDateString("it-IT");
    const daysText = batch.is_expired 
      ? `Scaduto da ${Math.abs(batch.days_left)} giorni` 
      : batch.days_left === 0 
        ? "Scade OGGI" 
        : `Scade tra ${batch.days_left} giorni`;

    return (
      <div className={`border rounded-lg p-4 ${bgColor}`} data-testid={`batch-${batch.id}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold truncate">{batch.product_name}</p>
            <p className="text-zinc-500 text-sm">{batch.category_name}</p>
          </div>
          <div className="text-right mr-4 flex items-center gap-4">
            <div>
              <p className="text-zinc-400 text-xs">Lotto</p>
              <p className="text-white font-mono font-semibold text-sm">{batch.lotto}</p>
            </div>
            <div>
              <p className="text-zinc-400 text-xs">Scadenza</p>
              <p className={`font-semibold ${textColor}`}>{scadFormatted}</p>
            </div>
            <div>
              <p className="text-zinc-400 text-xs">Pezzi</p>
              <p className="text-white font-semibold">{batch.quantita_disponibile}</p>
            </div>
            <div className="w-28">
              <p className={`text-xs font-medium ${textColor}`}>{daysText}</p>
            </div>
          </div>
          <button
            onClick={() => handleDeleteBatch(batch.product_id, batch.id)}
            className="text-red-400 hover:text-red-300 p-2"
            title="Elimina lotto"
            data-testid={`delete-batch-${batch.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-scadenze">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-white">Scadenze</h1>
          <span className="text-zinc-400 text-sm">
            Tutti i lotti in magazzino ({batches.length})
          </span>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
            <XCircle className="w-8 h-8 text-red-500 shrink-0" />
            <div>
              <p className="text-red-500 text-2xl font-bold">{expired.length}</p>
              <p className="text-zinc-400 text-sm">Scaduti</p>
            </div>
          </div>
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-orange-500 shrink-0" />
            <div>
              <p className="text-orange-500 text-2xl font-bold">{expiringSoon.length}</p>
              <p className="text-zinc-400 text-sm">Entro 5 giorni</p>
            </div>
          </div>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-center gap-3">
            <Clock className="w-8 h-8 text-yellow-500 shrink-0" />
            <div>
              <p className="text-yellow-500 text-2xl font-bold">{expiringMid.length}</p>
              <p className="text-zinc-400 text-sm">6-12 giorni</p>
            </div>
          </div>
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-500 shrink-0" />
            <div>
              <p className="text-green-500 text-2xl font-bold">{safe.length}</p>
              <p className="text-zinc-400 text-sm">Oltre 12 giorni</p>
            </div>
          </div>
        </div>

        {batches.length === 0 ? (
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 text-center">
            <Package className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-white font-semibold text-lg">Nessun lotto in magazzino</p>
            <p className="text-zinc-400 text-sm mt-1">Aggiungi lotti dalla sezione Magazzino</p>
          </div>
        ) : (
          <>
            {expired.length > 0 && (
              <div>
                <h2 className="text-red-400 font-bold text-lg mb-3 flex items-center gap-2">
                  <XCircle className="w-5 h-5" /> SCADUTI
                </h2>
                <div className="space-y-2">
                  {expired.map(b => <BatchRow key={b.id} batch={b} />)}
                </div>
              </div>
            )}

            {expiringSoon.length > 0 && (
              <div>
                <h2 className="text-orange-400 font-bold text-lg mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> IN SCADENZA (0-5 giorni)
                </h2>
                <div className="space-y-2">
                  {expiringSoon.map(b => <BatchRow key={b.id} batch={b} />)}
                </div>
              </div>
            )}

            {expiringMid.length > 0 && (
              <div>
                <h2 className="text-yellow-400 font-bold text-lg mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5" /> IN SCADENZA (6-12 giorni)
                </h2>
                <div className="space-y-2">
                  {expiringMid.map(b => <BatchRow key={b.id} batch={b} />)}
                </div>
              </div>
            )}

            {safe.length > 0 && (
              <div>
                <h2 className="text-green-400 font-bold text-lg mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" /> OK (oltre 12 giorni)
                </h2>
                <div className="space-y-2">
                  {safe.map(b => <BatchRow key={b.id} batch={b} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminScadenze;
